// API Base URL - Use environment variable or default to production backend
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://backend.tmfouzy.sg/api';

export const resolveMediaUrl = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  const origin = API_BASE_URL.replace(/\/api\/?$/, '');
  return `${origin}${url.startsWith('/') ? url : `/${url}`}`;
};

const REQUEST_TIMEOUT_MS = 30000;

const formatApiError = (errorData) => {
  if (!errorData) return 'Unknown error occurred';
  if (typeof errorData === 'string') return errorData;
  if (errorData.error) return errorData.error;
  if (errorData.detail) return errorData.detail;
  const lines = [];
  Object.entries(errorData).forEach(([field, msgs]) => {
    const label = field === 'non_field_errors' ? '' : `${field}: `;
    const msg = Array.isArray(msgs) ? msgs.join(', ') : String(msgs);
    lines.push(`${label}${msg}`);
  });
  return lines.join('\n') || 'Request failed';
};

const parseJsonSafe = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  const text = await response.text();
  if (!text) return null;
  if (!contentType.includes('application/json')) {
    if (!response.ok) {
      throw new Error(response.status === 404 ? 'Not found' : `Server error (${response.status})`);
    }
    return text;
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new Error('Invalid response from server. Please try again.');
  }
};

export const fetchJson = async (url, options = {}) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), options.timeout || REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    const data = await parseJsonSafe(response);
    if (!response.ok) {
      throw new Error(formatApiError(data) || `Request failed (${response.status})`);
    }
    return data;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }
    if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
      throw new Error('Unable to connect to the server. Please check your internet connection.');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

export const api = {
  // Categories
  getCategories: async () => fetchJson(`${API_BASE_URL}/categories/`),

  // Packages
  getPackages: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    return fetchJson(`${API_BASE_URL}/packages/?${params}`);
  },

  getFeaturedPackages: async () => fetchJson(`${API_BASE_URL}/packages/featured/`),

  getPackageBySlug: async (slug) => fetchJson(`${API_BASE_URL}/packages/${slug}/`),

  // Items
  getItems: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    return fetchJson(`${API_BASE_URL}/items/?${params}`);
  },

  // Tags
  getTags: async () => fetchJson(`${API_BASE_URL}/tags/`),

  // AI Chat Assistant
  sendChatMessage: async (message) => {
    const response = await fetch(`${API_BASE_URL}/chat/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message }),
    });
    if (!response.ok) {
      throw new Error('AI service unavailable');
    }
    return response.json();
  },

  // Booking
  createBooking: async (bookingData) => {
    const formData = new FormData();
    
    // Add all fields to FormData
    formData.append('package_id', bookingData.package_id);
    formData.append('rooms', JSON.stringify(bookingData.rooms));
    formData.append('contact_info', JSON.stringify(bookingData.contact_info));
    formData.append('emergency_contact', JSON.stringify(bookingData.emergency_contact));
    
    // Prepare passengers data without files
    const passengersData = bookingData.passengers.map((p, index) => {
      const { passport_photo, photo_id, ...passengerWithoutFiles } = p;
      return passengerWithoutFiles;
    });
    formData.append('passengers', JSON.stringify(passengersData));
    
    // Add passenger files separately
    bookingData.passengers.forEach((passenger, index) => {
      if (passenger.passport_photo) {
        formData.append(`passenger_${index}_passport_photo`, passenger.passport_photo);
      }
      if (passenger.photo_id) {
        formData.append(`passenger_${index}_photo_id`, passenger.photo_id);
      }
    });
    
    formData.append('addons', JSON.stringify(bookingData.addons || []));
    formData.append('payment_amount', bookingData.payment_amount);
    formData.append('payment_method', bookingData.payment_method);

    if (bookingData.discount_code) {
      formData.append('discount_code', bookingData.discount_code);
    }
    
    // Add payment screenshot if exists
    if (bookingData.payment_screenshot) {
      formData.append('payment_screenshot', bookingData.payment_screenshot);
    }
    
    const response = await fetch(`${API_BASE_URL}/create-booking/`, {
      method: 'POST',
      body: formData,
    });
    const data = await parseJsonSafe(response);
    if (!response.ok) {
      throw new Error(formatApiError(data) || 'Unable to create booking');
    }
    return data;
  },

  // Customer Portal
  getCustomerBookings: async (email) => {
    return fetchJson(`${API_BASE_URL}/bookings/?email=${encodeURIComponent(email)}`);
  },

  addPayment: async (bookingId, paymentData, email) => {
    const formData = new FormData();
    formData.append('amount', paymentData.amount);
    formData.append('payment_method', paymentData.payment_method);
    
    // Add file if exists
    if (paymentData.payment_screenshot) {
      formData.append('payment_screenshot', paymentData.payment_screenshot);
    }
    
    const response = await fetch(`${API_BASE_URL}/bookings/${bookingId}/add_payment/?email=${encodeURIComponent(email)}`, {
      method: 'POST',
      body: formData,
    });
    const data = await parseJsonSafe(response);
    if (!response.ok) {
      throw new Error(formatApiError(data) || 'Payment failed');
    }
    return data;
  },

  getCustomer: async (email) => {
    return fetchJson(`${API_BASE_URL}/customers/?email=${encodeURIComponent(email)}`);
  },

  updateCustomer: async (customerId, customerData) => {
    return fetchJson(`${API_BASE_URL}/customers/${customerId}/`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(customerData),
    });
  },

  // Get customer item orders
  getCustomerItemOrders: async (email) => {
    const data = await fetchJson(`${API_BASE_URL}/item-orders/?email=${encodeURIComponent(email)}`);
    return Array.isArray(data) ? data : (data.results || []);
  },

  // Create item order
  createItemOrder: async (orderData) => {
    const formData = new FormData();
    
    formData.append('customer_email', orderData.customer_email);
    formData.append('items', JSON.stringify(orderData.items));
    formData.append('shipping_name', orderData.shipping_info.name || '');
    formData.append('shipping_address', orderData.shipping_info.address);
    formData.append('shipping_unit', orderData.shipping_info.unit || '');
    formData.append('shipping_postal', orderData.shipping_info.postal_code);
    formData.append('shipping_phone', orderData.shipping_info.phone || '');
    formData.append('total_amount', orderData.total_amount);
    
    if (orderData.payment_screenshot) {
      formData.append('payment_screenshot', orderData.payment_screenshot);
    }
    
    const response = await fetch(`${API_BASE_URL}/create-item-order/`, {
      method: 'POST',
      body: formData,
    });
    const errOrData = await parseJsonSafe(response);
    if (!response.ok) {
      throw new Error(formatApiError(errOrData));
    }
    return errOrData;
  },

  // Validate discount code
  validateDiscountCode: async (code, amount) => {
    return fetchJson(`${API_BASE_URL}/validate-discount/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, amount }),
    });
  },

  // Customer documents
  getCustomerDocuments: async (email) => {
    const data = await fetchJson(`${API_BASE_URL}/customer-documents/?email=${encodeURIComponent(email)}`);
    return Array.isArray(data) ? data : (data.results || []);
  },

  uploadCustomerDocument: async ({ email, customer_id, document_type, title, description, file }) => {
    const formData = new FormData();
    if (customer_id) formData.append('customer', customer_id);
    if (email) formData.append('email', email);
    formData.append('document_type', document_type);
    formData.append('title', title);
    formData.append('description', description || '');
    formData.append('file', file);
    return fetchJson(`${API_BASE_URL}/customer-documents/upload/`, {
      method: 'POST',
      body: formData,
    });
  },

  // Blog
  getBlogPosts: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const data = await fetchJson(`${API_BASE_URL}/blog/?${params}`);
    return Array.isArray(data) ? data : (data.results || []);
  },

  getFeaturedBlogPosts: async () => {
    const data = await fetchJson(`${API_BASE_URL}/blog/featured/`);
    return Array.isArray(data) ? data : (data.results || []);
  },

  getLatestBlogPosts: async (limit = 6) => {
    const data = await fetchJson(`${API_BASE_URL}/blog/latest/?limit=${limit}`);
    return Array.isArray(data) ? data : (data.results || []);
  },

  getBlogPostBySlug: async (slug) => fetchJson(`${API_BASE_URL}/blog/${slug}/`),

  getBlogCategories: async () => fetchJson(`${API_BASE_URL}/blog/categories/`),

  getPageContent: async (slug) => fetchJson(`${API_BASE_URL}/pages/${slug}/`),
};
export const adminApi = {
  // Admin Login
  login: async (username, password) => {
    const response = await fetch(`${API_BASE_URL}/admin/login/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(formatApiError(error));
    }
    return response.json();
  },

  // Get all bookings (admin)
  getBookings: async () => {
    const response = await fetch(`${API_BASE_URL}/bookings/`);
    return response.json();
  },

  // Get all orders (admin)
  getOrders: async () => {
    const response = await fetch(`${API_BASE_URL}/item-orders/`);
    return response.json();
  },

  // Update booking status
  updateBookingStatus: async (bookingId, status) => {
    const response = await fetch(`${API_BASE_URL}/bookings/${bookingId}/update_status/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    });
    return response.json();
  },

  // Update order status
  updateOrderStatus: async (orderId, status) => {
    const response = await fetch(`${API_BASE_URL}/item-orders/${orderId}/update_status/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    });
    return response.json();
  },

  // Delete booking
  deleteBooking: async (bookingId) => {
    const response = await fetch(`${API_BASE_URL}/bookings/${bookingId}/`, {
      method: 'DELETE'
    });
    return response.ok;
  },

  // Delete order
  deleteOrder: async (orderId) => {
    const response = await fetch(`${API_BASE_URL}/item-orders/${orderId}/`, {
      method: 'DELETE'
    });
    return response.ok;
  },

  // Get ALL packages including inactive (for admin)
  getAllPackages: async () => {
    const response = await fetch(`${API_BASE_URL}/admin/packages/`);
    const data = await response.json();
    return Array.isArray(data) ? data : (data.results || []);
  },

  // Package CRUD
  createPackage: async (packageData) => {
    // Support both FormData (file upload) and plain object
    const isFormData = packageData instanceof FormData;
    const response = await fetch(`${API_BASE_URL}/admin/packages/`, {
      method: 'POST',
      ...(isFormData ? {} : { headers: { 'Content-Type': 'application/json' } }),
      body: isFormData ? packageData : JSON.stringify(packageData)
    });
    if (!response.ok) {
      let errorMsg = `Server error (${response.status})`;
      try {
        const ct = response.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          const errorData = await response.json();
          errorMsg = formatApiError(errorData);
        }
      } catch (_) {}
      throw new Error(errorMsg);
    }
    return response.json();
  },

  updatePackage: async (packageId, packageData) => {
    // Support both FormData (file upload) and plain object
    const isFormData = packageData instanceof FormData;
    const response = await fetch(`${API_BASE_URL}/admin/packages/${packageId}/`, {
      method: 'PATCH',
      ...(isFormData ? {} : { headers: { 'Content-Type': 'application/json' } }),
      body: isFormData ? packageData : JSON.stringify(packageData)
    });
    if (!response.ok) {
      let errorMsg = `Server error (${response.status})`;
      try {
        const ct = response.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          const errorData = await response.json();
          errorMsg = formatApiError(errorData);
        }
      } catch (_) {}
      throw new Error(errorMsg);
    }
    return response.json();
  },

  deletePackage: async (packageId) => {
    const response = await fetch(`${API_BASE_URL}/admin/packages/${packageId}/`, {
      method: 'DELETE'
    });
    return response.ok;
  },

  // Get ALL categories including inactive (for admin)
  getAllCategories: async () => {
    const response = await fetch(`${API_BASE_URL}/admin/categories/`);
    const data = await response.json();
    return Array.isArray(data) ? data : (data.results || []);
  },

  // Category CRUD
  createCategory: async (categoryData) => {
    const isFormData = categoryData instanceof FormData;
    const response = await fetch(`${API_BASE_URL}/admin/categories/`, {
      method: 'POST',
      ...(isFormData ? {} : { headers: { 'Content-Type': 'application/json' } }),
      body: isFormData ? categoryData : JSON.stringify(categoryData)
    });
    if (!response.ok) throw new Error('Failed to create category');
    return response.json();
  },

  updateCategory: async (categoryId, categoryData) => {
    const isFormData = categoryData instanceof FormData;
    const response = await fetch(`${API_BASE_URL}/admin/categories/${categoryId}/`, {
      method: 'PUT',
      ...(isFormData ? {} : { headers: { 'Content-Type': 'application/json' } }),
      body: isFormData ? categoryData : JSON.stringify(categoryData)
    });
    if (!response.ok) throw new Error('Failed to update category');
    return response.json();
  },

  deleteCategory: async (categoryId) => {
    const response = await fetch(`${API_BASE_URL}/admin/categories/${categoryId}/`, {
      method: 'DELETE'
    });
    return response.ok;
  },

  // Item CRUD
  createItem: async (itemData) => {
    const isFormData = itemData instanceof FormData;
    const response = await fetch(`${API_BASE_URL}/admin/items/`, {
      method: 'POST',
      ...(isFormData ? {} : { headers: { 'Content-Type': 'application/json' } }),
      body: isFormData ? itemData : JSON.stringify(itemData)
    });
    if (!response.ok) throw new Error('Failed to create item');
    return response.json();
  },

  updateItem: async (itemId, itemData) => {
    const isFormData = itemData instanceof FormData;
    const response = await fetch(`${API_BASE_URL}/admin/items/${itemId}/`, {
      method: 'PUT',
      ...(isFormData ? {} : { headers: { 'Content-Type': 'application/json' } }),
      body: isFormData ? itemData : JSON.stringify(itemData)
    });
    if (!response.ok) throw new Error('Failed to update item');
    return response.json();
  },

  deleteItem: async (itemId) => {
    const response = await fetch(`${API_BASE_URL}/admin/items/${itemId}/`, {
      method: 'DELETE'
    });
    return response.ok;
  },

  // User Management
  getUsers: async () => {
    const response = await fetch(`${API_BASE_URL}/admin/users/`);
    return response.json();
  },

  createUser: async (userData) => {
    const response = await fetch(`${API_BASE_URL}/admin/users/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    });
    return response.json();
  },

  updateUser: async (userId, userData) => {
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    });
    return response.json();
  },

  deleteUser: async (userId) => {
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/`, {
      method: 'DELETE'
    });
    return response.ok;
  },

  // Customer Management
  getCustomers: async () => {
    const response = await fetch(`${API_BASE_URL}/admin/customers/`);
    return response.json();
  },

  getTourLeaders: async () => {
    const response = await fetch(`${API_BASE_URL}/admin/customers/tour_leaders/`);
    return response.json();
  },

  toggleTourLeader: async (customerId) => {
    const response = await fetch(`${API_BASE_URL}/admin/customers/${customerId}/toggle_tour_leader/`, {
      method: 'POST'
    });
    return response.json();
  },

  // Package Import/Export
  exportPackages: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/packages/export/`, {
        method: 'GET',
        headers: {
          'Accept': 'text/csv',
        }
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      // Get the blob data
      const blob = await response.blob();
      
      // Extract filename from Content-Disposition header
      const disposition = response.headers.get('Content-Disposition');
      let filename = 'packages_export.csv';
      if (disposition && disposition.includes('filename=')) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
        if (matches && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Export error:', error);
      throw new Error('Export failed');
    }
  },

  importPackages: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_BASE_URL}/packages/import/`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const error = await response.json();
          throw new Error(error.error || 'Import failed');
        } else {
          throw new Error(`Import failed with status ${response.status}`);
        }
      }
      
      return response.json();
    } catch (error) {
      console.error('Import error:', error);
      throw error;
    }
  },

  getPackagePassengers: async (packageId) => {
    const response = await fetch(`${API_BASE_URL}/packages/${packageId}/passengers/`);
    return response.json();
  },

  exportPackagePassengers: async (packageId) => {
    window.open(`${API_BASE_URL}/packages/${packageId}/passengers/export/`, '_blank');
  },

  // Payments
  getPayments: async () => {
    const response = await fetch(`${API_BASE_URL}/payments/`);
    return response.json();
  },

  updatePaymentStatus: async (paymentId, status) => {
    const response = await fetch(`${API_BASE_URL}/payments/${paymentId}/update_status/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    });
    return response.json();
  },

  // Customer Documents
  getAllCustomerDocuments: async () => {
    const data = await fetchJson(`${API_BASE_URL}/admin/customer-documents/`);
    return Array.isArray(data) ? data : (data.results || []);
  },

  getCustomerDocuments: async (customerEmail) => {
    const response = await fetch(`${API_BASE_URL}/customer-documents/?email=${customerEmail}`);
    return response.json();
  },

  uploadCustomerDocument: async (documentData) => {
    const formData = new FormData();
    formData.append('customer', documentData.customer_id);
    if (documentData.booking_id) {
      formData.append('booking', documentData.booking_id);
    }
    formData.append('document_type', documentData.document_type);
    formData.append('title', documentData.title);
    formData.append('description', documentData.description || '');
    formData.append('file', documentData.file);
    formData.append('is_important', documentData.is_important || false);
    if (documentData.expiry_date) {
      formData.append('expiry_date', documentData.expiry_date);
    }

    const response = await fetch(`${API_BASE_URL}/customer-documents/upload/`, {
      method: 'POST',
      body: formData
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to upload document');
    }
    
    return response.json();
  },

  deleteCustomerDocument: async (documentId) => {
    const response = await fetch(`${API_BASE_URL}/customer-documents/${documentId}/`, {
      method: 'DELETE'
    });
    return response.ok;
  },

  // Contact Messages (admin)
  getContactMessages: async () => {
    const response = await fetch(`${API_BASE_URL}/admin/contact-messages/`);
    const data = await response.json();
    return Array.isArray(data) ? data : (data.results || []);
  },

  markContactMessageRead: async (messageId, isRead = true) => {
    const response = await fetch(`${API_BASE_URL}/admin/contact-messages/${messageId}/`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_read: isRead })
    });
    return response.json();
  },

  deleteContactMessage: async (messageId) => {
    const response = await fetch(`${API_BASE_URL}/admin/contact-messages/${messageId}/`, {
      method: 'DELETE'
    });
    return response.ok;
  },

  // Discount Codes (admin)
  getDiscountCodes: async () => {
    const response = await fetch(`${API_BASE_URL}/admin/discount-codes/`);
    const data = await response.json();
    return Array.isArray(data) ? data : (data.results || []);
  },

  createDiscountCode: async (codeData) => {
    const response = await fetch(`${API_BASE_URL}/admin/discount-codes/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(codeData)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(formatApiError(error));
    }
    return response.json();
  },

  updateDiscountCode: async (codeId, codeData) => {
    const response = await fetch(`${API_BASE_URL}/admin/discount-codes/${codeId}/`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(codeData)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(formatApiError(error));
    }
    return response.json();
  },

  deleteDiscountCode: async (codeId) => {
    const response = await fetch(`${API_BASE_URL}/admin/discount-codes/${codeId}/`, {
      method: 'DELETE'
    });
    return response.ok;
  },

  // Blog Posts (admin)
  getBlogPosts: async () => {
    const data = await fetchJson(`${API_BASE_URL}/admin/blog/`);
    return Array.isArray(data) ? data : (data.results || []);
  },

  createBlogPost: async (postData) => {
    const isFormData = postData instanceof FormData;
    const response = await fetch(`${API_BASE_URL}/admin/blog/`, {
      method: 'POST',
      ...(isFormData ? {} : { headers: { 'Content-Type': 'application/json' } }),
      body: isFormData ? postData : JSON.stringify(postData),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(formatApiError(err) || 'Failed to create blog post');
    }
    return response.json();
  },

  updateBlogPost: async (postId, postData) => {
    const isFormData = postData instanceof FormData;
    const response = await fetch(`${API_BASE_URL}/admin/blog/${postId}/`, {
      method: 'PATCH',
      ...(isFormData ? {} : { headers: { 'Content-Type': 'application/json' } }),
      body: isFormData ? postData : JSON.stringify(postData),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(formatApiError(err) || 'Failed to update blog post');
    }
    return response.json();
  },

  deleteBlogPost: async (postId) => {
    const response = await fetch(`${API_BASE_URL}/admin/blog/${postId}/`, { method: 'DELETE' });
    return response.ok;
  },

  toggleBlogPublish: async (postId) => {
    const response = await fetch(`${API_BASE_URL}/admin/blog/${postId}/toggle_publish/`, { method: 'POST' });
    return response.json();
  },

  toggleBlogFeatured: async (postId) => {
    const response = await fetch(`${API_BASE_URL}/admin/blog/${postId}/toggle_featured/`, { method: 'POST' });
    return response.json();
  },

  getPageContent: async (slug) => fetchJson(`${API_BASE_URL}/admin/pages/${slug}/`),

  savePageContent: async (slug, content) => {
    const response = await fetch(`${API_BASE_URL}/admin/pages/${slug}/`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(formatApiError(err) || 'Failed to save page content');
    }
    return response.json();
  },

  uploadPageImage: async (file) => {
    const body = new FormData();
    body.append('image', file);
    const response = await fetch(`${API_BASE_URL}/admin/pages/upload-image/`, {
      method: 'POST',
      body,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(formatApiError(err) || 'Failed to upload image');
    }
    return response.json();
  },
};
